import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { DraftItem, itemProblems, parseBaht, satangToInput, tmpId } from '../lib/menuEdit';
import { photoUrls, uploadDishPhoto } from '../lib/photos';
import { baht, main, second } from '../lib/text';
import type { LocalText, OptionGroup, Station } from '../lib/types';
import { Shell } from './Shell';
import { handleAuthError, useStaff } from './useStaff';

interface Category { id: string; name: LocalText; sort: number }
interface Item extends DraftItem { id: string }
interface Draft { categories: Category[]; items: Item[]; soldOut: string[]; published: { version: number; publishedAt: string } | null; unpublishedChanges: boolean }

/** /menu: the owner's menu editor. Changes stay in the draft until "Publish". */
export function MenuEditor({ path }: { path: string }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [cat, setCat] = useState<string | null>(null);
  const [editing, setEditing] = useState<DraftItem | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [stations, setStations] = useState<Station[]>([]);
  const { s, online } = useStaff(() => undefined, () => undefined);
  const token = s?.token ?? '';

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [d, r] = await Promise.all([
        api<Draft>('GET', '/menu/draft', { token }),
        api<{ settings: { stations: Station[] } }>('GET', '/restaurant', { token }),
      ]);
      setStations(r.settings.stations);
      setDraft(d);
      setCat((c) => (c && d.categories.some((x) => x.id === c) ? c : d.categories[0]?.id ?? null));
    } catch (e) { handleAuthError(e); setMsg({ kind: 'err', text: (e as Error).message }); }
  }, [token]);
  useEffect(() => { void load(); }, [load]);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setMsg(null);
    try { await fn(); await load(); if (ok) setMsg({ kind: 'ok', text: ok }); }
    catch (e) { handleAuthError(e); setMsg({ kind: 'err', text: (e as Error).message }); }
    finally { setBusy(false); }
  };

  const addCategory = () => {
    const th = prompt('ชื่อหมวด (ไทย) · Category name (Thai)');
    if (!th?.trim()) return;
    const en = prompt('English name (optional)') ?? '';
    void run(() => api('POST', '/menu/categories', { token, body: { name: { th, en }, sort: (draft?.categories.length ?? 0) + 1 } }));
  };

  const publish = () => void run(async () => {
    const r = await api<{ version: number; items: number; warnings: { itemId: string }[] }>('POST', '/menu/publish', { token });
    if (r.warnings.length) setMsg({ kind: 'err', text: `Published v${r.version}; ${r.warnings.length} photo(s) still processing were left out` });
  }, 'เผยแพร่แล้ว ลูกค้าเห็นเมนูใหม่ · Published: guests see the new menu');

  const items = (draft?.items ?? []).filter((i) => i.categoryId === cat);
  const soldOut = new Set(draft?.soldOut ?? []);
  return (
    <Shell path={path} online={online} sub={draft?.published ? `menu v${draft.published.version}` : 'not published yet'}>
      <div className="panel row" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700 }}>
          {draft?.unpublishedChanges ? 'มีการแก้ไขที่ยังไม่เผยแพร่ · You have unpublished changes' : 'ลูกค้าเห็นเมนูล่าสุดแล้ว · Guests see the latest menu'}
        </span>
        <button className="btn-mango" disabled={busy || !draft?.unpublishedChanges} onClick={publish}>เผยแพร่ · Publish</button>
      </div>
      {msg && <div className={msg.kind === 'err' ? 'warn' : 'ticket'} role="status" style={{ color: 'var(--ink)', marginBottom: 12 }}>{msg.text}</div>}

      <div className="layout">
        <aside className="aside panel side" style={{ flexBasis: 240 }}>
          <h2 className="display" style={{ margin: 0, fontSize: 18 }}>หมวด · Categories</h2>
          {(draft?.categories ?? []).map((c) => (
            <div key={c.id} className="row" style={{ gap: 6 }}>
              <button className="tab" aria-current={c.id === cat ? 'page' : undefined} style={{ flex: 1, textAlign: 'left' }} onClick={() => setCat(c.id)}>
                {main(c.name)} <span style={{ opacity: .7 }}>{second(c.name)}</span>
              </button>
              <button className="btn-dark" aria-label={`Delete ${main(c.name)}`} disabled={busy} onClick={() => confirm(`Delete "${main(c.name)}"? It must be empty.`) && void run(() => api('DELETE', `/menu/categories/${c.id}`, { token }))}>×</button>
            </div>
          ))}
          <button className="btn-dark" onClick={addCategory} disabled={busy}>+ เพิ่มหมวด · Add category</button>
        </aside>

        <section className="main">
          {!cat ? (
            <div className="panel"><p style={{ margin: 0 }}>เริ่มจากเพิ่มหมวดหมู่ · Start by adding a category (e.g. อาหารจานเดียว, เครื่องดื่ม).</p></div>
          ) : (
            <div className="tiles" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
              {items.map((it) => (
                <article key={it.id} className="kt" style={{ marginBottom: 0 }}>
                  <ItemThumb token={token} photoId={it.photoId} />
                  <div><div className="name-th">{main(it.name)}</div><div className="name-en">{second(it.name)}</div></div>
                  <div className="row"><span className="price">{baht(it.priceSatang)}</span><span className="muted" style={{ fontSize: 12 }}>{it.options.length ? `${it.options.length} options` : ''}</span></div>
                  <div className="row" style={{ gap: 8 }}>
                    <button className="kbtn ready" style={{ flex: 1, minHeight: 44, padding: 6 }} onClick={() => setEditing(it)}>แก้ไข · Edit</button>
                    <button className={`kbtn ${soldOut.has(it.id) ? 'new' : 'ready'}`} style={{ flex: 1, minHeight: 44, padding: 6 }} aria-pressed={soldOut.has(it.id)}
                      onClick={() => void run(() => api('PUT', `/menu/items/${it.id}/availability`, { token, body: { available: soldOut.has(it.id) } }))}>
                      {soldOut.has(it.id) ? 'หมด · Sold out' : 'มีขาย · Available'}
                    </button>
                  </div>
                </article>
              ))}
              <button className="tile free" style={{ justifyContent: 'center', alignItems: 'center', minHeight: 200 }}
                onClick={() => setEditing({ categoryId: cat, name: {}, priceSatang: 0, options: [], sort: items.length + 1 })}>
                <span className="display" style={{ fontSize: 18 }}>+ เพิ่มเมนู · Add dish</span>
              </button>
            </div>
          )}
        </section>
      </div>

      {editing && (
        <ItemSheet token={token} item={editing} categories={draft?.categories ?? []} stations={stations} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); setMsg({ kind: 'ok', text: 'บันทึกแล้ว (ยังไม่เผยแพร่) · Saved to the draft' }); }} />
      )}
    </Shell>
  );
}

function ItemThumb({ token, photoId }: { token: string; photoId?: string }) {
  const [src, setSrc] = useState<string | undefined>();
  useEffect(() => { if (photoId && token) void photoUrls(token, photoId).then((u) => setSrc(u.sticker ?? u.photo), () => undefined); }, [photoId, token]);
  return (
    <div style={{ display: 'flex', justifyContent: 'center', minHeight: 110, alignItems: 'center' }}>
      {src ? <img className="sticker" src={src} alt="" style={{ width: 110, height: 110 }} /> : <div className="plate"><span /></div>}
    </div>
  );
}

/** Add / edit one dish: names, price, photo (-> sticker), options. */
function ItemSheet({ token, item, categories, stations, onClose, onSaved }: { token: string; item: DraftItem; categories: Category[]; stations: Station[]; onClose: () => void; onSaved: () => void }) {
  const [it, setIt] = useState<DraftItem>(structuredClone(item));
  const [price, setPrice] = useState(item.id ? satangToInput(item.priceSatang) : '');
  const [photo, setPhoto] = useState<{ stage: string; sticker?: string; photo?: string; error?: string }>({ stage: item.photoId ? 'ready' : 'none' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (item.photoId) void photoUrls(token, item.photoId).then((u) => setPhoto({ stage: u.status, sticker: u.sticker, photo: u.photo }), () => undefined);
  }, [item.photoId, token]);

  const priceSatang = parseBaht(price);
  const problems = [...itemProblems({ ...it, priceSatang: priceSatang ?? 0 }), ...(priceSatang === null ? ['ราคาไม่ถูกต้อง · Enter a price like 129 or 129.50'] : [])];
  const setName = (lang: 'th' | 'en', v: string) => setIt({ ...it, name: { ...it.name, [lang]: v } });
  const setGroup = (i: number, g: OptionGroup) => setIt({ ...it, options: it.options.map((x, j) => (j === i ? g : x)) });

  const onFile = async (f?: File) => {
    if (!f) return;
    setPhoto({ stage: 'uploading' });
    try {
      const st = await uploadDishPhoto(token, f, (stage) => setPhoto((p) => ({ ...p, stage })));
      if (st.status === 'failed') throw new Error('ไฟล์นี้ใช้ไม่ได้ · This file could not be read as a photo');
      const u = await photoUrls(token, st.photoId);
      setIt((x) => ({ ...x, photoId: st.photoId }));
      setPhoto({ stage: st.status, sticker: u.sticker, photo: u.photo });
    } catch (e) { setPhoto({ stage: 'none', error: (e as Error).message }); }
  };

  const save = async () => {
    if (problems.length || priceSatang === null) return;
    setBusy(true);
    setErr(null);
    const body = { ...it, priceSatang, id: undefined };
    try {
      if (it.id) await api('PUT', `/menu/items/${it.id}`, { token, body });
      else await api('POST', '/menu/items', { token, body });
      onSaved();
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  const remove = async () => {
    if (!it.id || !confirm(`Delete "${main(it.name)}"?`)) return;
    setBusy(true);
    try { await api('DELETE', `/menu/items/${it.id}`, { token }); onSaved(); } catch (e) { setErr((e as Error).message); setBusy(false); }
  };

  return (
    <div className="sheet-wrap" onClick={onClose}>
      <section className="sheet" role="dialog" aria-modal="true" aria-label="Edit dish" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--ink)' }}>
        <div className="row">
          <h2 className="display" style={{ margin: 0, fontSize: 22 }}>{it.id ? 'แก้ไขเมนู · Edit dish' : 'เมนูใหม่ · New dish'}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="ticket" style={{ alignItems: 'center' }}>
          {photo.sticker || photo.photo ? (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {photo.photo && <img src={photo.photo} alt="Real photo" style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 10, border: '2px solid var(--ink)' }} />}
              {photo.sticker && <img className="sticker" src={photo.sticker} alt="Sticker" style={{ width: 110, height: 110 }} />}
            </div>
          ) : <div className="plate"><span /></div>}
          <span className="muted" style={{ fontSize: 13 }}>
            {photo.stage === 'uploading' ? 'กำลังอัปโหลด… · Uploading…' : photo.stage === 'processing' ? 'กำลังทำสติกเกอร์… · Making the sticker…'
              : photo.stage === 'no-subject' ? 'หาจานในรูปไม่เจอ ใช้รูปจริงแทน · No dish found; the real photo will be shown' : 'รูปจริงของจานนี้ · A real photo of this dish'}
          </span>
          {photo.error && <span className="warn" role="alert">{photo.error}</span>}
          <label className="cta light" style={{ textAlign: 'center' }}>
            {photo.stage === 'none' ? 'อัปโหลดรูป · Upload photo' : 'เปลี่ยนรูป · Change photo'}
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => void onFile(e.target.files?.[0])} disabled={photo.stage === 'uploading' || photo.stage === 'processing'} />
          </label>
        </div>

        <label className="field">ชื่อ (ไทย) · Name in Thai<input value={it.name.th ?? ''} maxLength={80} onChange={(e) => setName('th', e.target.value)} /></label>
        <label className="field">Name in English<input value={it.name.en ?? ''} maxLength={80} onChange={(e) => setName('en', e.target.value)} /></label>
        <label className="field">ราคา (บาท) · Price (฿)<input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="129" /></label>
        <label className="field">หมวด · Category
          <select value={it.categoryId} onChange={(e) => setIt({ ...it, categoryId: e.target.value })} style={{ border: 'var(--line)', borderRadius: 12, padding: 10, font: 'inherit', minHeight: 44 }}>
            {categories.map((c) => <option key={c.id} value={c.id}>{main(c.name)}</option>)}
          </select>
        </label>

        {stations.length > 1 && (
          <label className="field">จุดในครัว · Kitchen station
            <select value={it.station ?? stations[0].id} onChange={(e) => setIt({ ...it, station: e.target.value })} style={{ border: 'var(--line)', borderRadius: 12, padding: 10, font: 'inherit', minHeight: 44 }}>
              {stations.map((st) => <option key={st.id} value={st.id}>{main(st.name)}</option>)}
            </select>
          </label>
        )}

        <h3 className="label" style={{ margin: '6px 0 0' }}>ตัวเลือก · Options</h3>
        {it.options.map((g, i) => (
          <fieldset key={g.id} className="ticket" style={{ gap: 8 }}>
            <legend className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Option group {i + 1}</legend>
            <div className="row" style={{ gap: 8 }}>
              <input aria-label="Group name (Thai)" placeholder="ความเผ็ด" value={g.name.th ?? ''} onChange={(e) => setGroup(i, { ...g, name: { ...g.name, th: e.target.value } })} style={{ flex: 1, border: 'var(--line)', borderRadius: 10, padding: 8, font: 'inherit' }} />
              <input aria-label="Group name (English)" placeholder="Spice" value={g.name.en ?? ''} onChange={(e) => setGroup(i, { ...g, name: { ...g.name, en: e.target.value } })} style={{ flex: 1, border: 'var(--line)', borderRadius: 10, padding: 8, font: 'inherit' }} />
              <button className="icon-btn" aria-label="Remove option group" onClick={() => setIt({ ...it, options: it.options.filter((_, j) => j !== i) })}>×</button>
            </div>
            <div className="row" style={{ gap: 8, justifyContent: 'flex-start', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 600 }}>
                <input type="checkbox" checked={g.min > 0} onChange={(e) => setGroup(i, { ...g, min: e.target.checked ? 1 : 0, max: Math.max(1, g.max) })} /> ต้องเลือก · Required
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 600 }}>
                เลือกได้สูงสุด · Up to
                <input type="number" min={1} max={20} value={g.max} onChange={(e) => setGroup(i, { ...g, max: Math.max(1, Math.min(20, Number(e.target.value) || 1)) })} style={{ width: 60, border: 'var(--line)', borderRadius: 10, padding: 6, font: 'inherit' }} />
              </label>
            </div>
            {g.choices.map((c, k) => (
              <div key={c.id} className="row" style={{ gap: 6 }}>
                <input aria-label="Choice (Thai)" placeholder="เผ็ดมาก" value={c.name.th ?? ''} onChange={(e) => setGroup(i, { ...g, choices: g.choices.map((x, m) => (m === k ? { ...x, name: { ...x.name, th: e.target.value } } : x)) })} style={{ flex: 2, minWidth: 0, border: '2px solid var(--ink)', borderRadius: 10, padding: 8, font: 'inherit' }} />
                <input aria-label="Choice (English)" placeholder="Thai hot" value={c.name.en ?? ''} onChange={(e) => setGroup(i, { ...g, choices: g.choices.map((x, m) => (m === k ? { ...x, name: { ...x.name, en: e.target.value } } : x)) })} style={{ flex: 2, minWidth: 0, border: '2px solid var(--ink)', borderRadius: 10, padding: 8, font: 'inherit' }} />
                <input aria-label="Extra price (฿)" placeholder="+฿" inputMode="decimal" defaultValue={c.priceSatang ? satangToInput(c.priceSatang) : ''} onChange={(e) => setGroup(i, { ...g, choices: g.choices.map((x, m) => (m === k ? { ...x, priceSatang: parseBaht(e.target.value || '0') ?? 0 } : x)) })} style={{ flex: 1, minWidth: 0, border: '2px solid var(--ink)', borderRadius: 10, padding: 8, font: 'inherit' }} />
                <button className="icon-btn" style={{ flex: 'none' }} aria-label="Remove choice" onClick={() => setGroup(i, { ...g, choices: g.choices.filter((_, m) => m !== k), max: Math.max(1, Math.min(g.max, g.choices.length - 1)) })}>×</button>
              </div>
            ))}
            <button className="ghost" style={{ color: 'var(--ink)', borderColor: 'var(--ink)' }} onClick={() => setGroup(i, { ...g, choices: [...g.choices, { id: tmpId('c'), name: {}, priceSatang: 0 }] })}>+ ตัวเลือก · Add choice</button>
          </fieldset>
        ))}
        <button className="ghost" style={{ color: 'var(--ink)', borderColor: 'var(--ink)' }} onClick={() => setIt({ ...it, options: [...it.options, { id: tmpId('g'), name: {}, min: 0, max: 1, choices: [{ id: tmpId('c'), name: {}, priceSatang: 0 }] }] })}>
          + กลุ่มตัวเลือก (เช่น ความเผ็ด, ขนาด) · Add option group
        </button>

        {problems.length > 0 && <ul className="warn" style={{ margin: 0, paddingLeft: 28 }}>{problems.map((p) => <li key={p}>{p}</li>)}</ul>}
        {err && <div className="warn" role="alert">{err}</div>}
        <div className="row" style={{ gap: 10 }}>
          {it.id && <button className="ghost" style={{ color: 'var(--red)', borderColor: 'var(--red)' }} onClick={() => void remove()} disabled={busy}>ลบ · Delete</button>}
          <button className="cta light" style={{ flex: 1 }} disabled={busy || problems.length > 0 || photo.stage === 'uploading' || photo.stage === 'processing'} onClick={() => void save()}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก · Save'}
          </button>
        </div>
      </section>
    </div>
  );
}
